import { z } from 'zod'
import { DiscountResponseSchema, OrderResponseSchema } from './OrderSchema'
import { OrderScenario } from '../types'
import { CofidisApplicationFormPayloadSchema } from './CofidisApplicationFormPayloadSchema'

export const PaymentMethod = {
  terminal: 'terminal',
  invoice: 'invoice',
  paylink: 'paylink',
  monthly: 'monthly',
} as const

export const isValidPaymentMethod = (value: string): value is PaymentMethod => {
  return Object.values(PaymentMethod).includes(value as PaymentMethod)
}

export const PrePayConfigProviders = {
  cofidisApplication: 'cofidis',
} as const

type PrePayProviderName =
  (typeof PrePayConfigProviders)[keyof typeof PrePayConfigProviders]

export const getPrePayProviderName = (key: string) => {
  if (key in PrePayConfigProviders) {
    return PrePayConfigProviders[
      key as keyof typeof PrePayConfigProviders
    ] as PrePayProviderName
  }
  return undefined
}

export const PaymentProvider = {
  stripe: 'stripe',
  cofidis: 'cofidis',
  cofidisApplication: 'cofidisApplication',
  alior: 'alior',
  payla: 'payla',
  sepa: 'sepa',
  unzer: 'unzer',
  tap: 'tap',
  hasabe: 'hasabe',
} as const

export type PaymentStatus =
  | 'draft'
  | 'completed'
  | 'initialized'
  | 'waiting'
  | 'error'

export type PaymentRowsSettings = {
  prepayment?: boolean
  collect?: boolean
}

export type ScenarioSettings = {
  custom?: CustomAmountMode
  rows?: PaymentRowsSettings
}

export type CustomAmountMode = 'enabled' | 'disabled' | 'hidden'

export type PaymentSettings = {
  custom: CustomAmountMode
  updates: 'poll' | null
  iban?: boolean
  privacy?: boolean
  terms?: boolean
  sepa?: boolean
  qrcode?: boolean
  signature?: boolean
  createStatus: PaymentStatus
  rows?: PaymentRowsSettings
  minTotal?: number
  canCancel?: boolean
}

type PaymentTypeSettings = PaymentSettings & {
  batch?: boolean
  scenarios?: Partial<Record<OrderScenario, ScenarioSettings>>
}

export type PaymentProvider =
  (typeof PaymentProvider)[keyof typeof PaymentProvider]

export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod]

export const getProviderMethod = (
  provider: PaymentProvider,
  method: PaymentMethod
) => {
  return `${provider}:${method}`
}

export const ValidPaymentProviders = [
  'stripe:terminal',
  'stripe:paylink',
  'payla:invoice',
  'sepa:monthly',
  'unzer:monthly',
  'cofidis:monthly',
  'cofidisApplication:monthly',
  'alior:monthly',
  'tap:paylink',
  'hasabe:terminal',
  'hasabe:paylink',
] as const

export type ValidProviderMethod = (typeof ValidPaymentProviders)[number]

export const isValidProviderMethod = (
  key: string
): key is ValidProviderMethod => {
  return ValidPaymentProviders.includes(key as ValidProviderMethod)
}

const IbanMonthlySettings = {
  custom: 'hidden',
  updates: null,
  iban: true,
  privacy: true,
  terms: true,
  sepa: true,
  signature: true,
  createStatus: 'completed',
  minTotal: 120,
} as const satisfies PaymentTypeSettings

export const ProviderMethodSettings: Record<
  ValidProviderMethod,
  PaymentTypeSettings
> = {
  'stripe:terminal': {
    custom: 'enabled',
    updates: 'poll',
    createStatus: 'waiting',
    canCancel: true,
    scenarios: {
      'base.simplified': {
        custom: 'disabled',
      },
    },
  },
  'stripe:paylink': {
    custom: 'enabled',
    updates: 'poll',
    qrcode: true,
    createStatus: 'waiting',
    scenarios: {
      'base.simplified': {
        custom: 'disabled',
      },
    },
  },
  'payla:invoice': {
    custom: 'hidden',
    updates: null,
    createStatus: 'initialized',
  },
  'sepa:monthly': { ...IbanMonthlySettings },
  'unzer:monthly': { ...IbanMonthlySettings },
  'cofidis:monthly': {
    custom: 'hidden',
    updates: 'poll',
    qrcode: true,
    createStatus: 'initialized',
    batch: true,
  },
  'cofidisApplication:monthly': {
    custom: 'hidden',
    updates: 'poll',
    qrcode: true,
    createStatus: 'initialized',
    batch: true,
  },
  'alior:monthly': {
    custom: 'hidden',
    updates: 'poll',
    qrcode: true,
    createStatus: 'initialized',
    batch: true,
  },
  'tap:paylink': {
    custom: 'enabled',
    updates: 'poll',
    qrcode: true,
    createStatus: 'waiting',
  },
  'hasabe:terminal': {
    custom: 'enabled',
    updates: 'poll',
    createStatus: 'waiting',
    canCancel: false,
  },
  'hasabe:paylink': {
    custom: 'enabled',
    updates: 'poll',
    qrcode: true,
    createStatus: 'waiting',
  },
}

export type RegionProviderSettings = Record<PaymentMethod, PaymentProvider[]>

export const PaymentMethodDisableReason = {
  min_value: 'min_value',
  merged: 'merged',
  disabled: 'disabled',
} as const

export type PaymentMethodDisableReason =
  (typeof PaymentMethodDisableReason)[keyof typeof PaymentMethodDisableReason]

export type PaymentMethodAvailability = {
  method: PaymentMethod
  available: boolean
  disableReasons: PaymentMethodDisableReason[]
}

export const getProviderMethodSettings = (
  providerMethod: ValidProviderMethod
) => ProviderMethodSettings[providerMethod]

export const getMethodMinTotal = (method: PaymentMethod): number => {
  return ValidPaymentProviders.filter((pm) => pm.endsWith(`:${method}`)).reduce(
    (max, pm) => Math.max(max, ProviderMethodSettings[pm].minTotal ?? 0),
    0
  )
}

export const PaymentMethodSchema = z.object({
  key: z.nativeEnum(PaymentMethod),
  name: z.string(),
})

const PAYMENT_METHODS = Object.values(PaymentMethod)

export const isPaymentMethod = (value: string): value is PaymentMethod => {
  return PAYMENT_METHODS.includes(value as PaymentMethod)
}

// Prepay keys that are not payment methods or providers
export const PrePayExtraMethods = {
  globemed: 'globemed',
} as const

export const PrePaySettingsSchema = z.object({
  key: z
    .nativeEnum(PaymentMethod)
    .or(z.nativeEnum(PrePayConfigProviders))
    .or(z.enum(PrePayExtraMethods)),
  name: z.string(),
})

export type PrePaySettings = z.infer<typeof PrePaySettingsSchema>

export const splitPrePayMethods = (items: readonly unknown[]) => {
  const methods: PrePaySettings[] = []
  const unsupported: unknown[] = []
  items.forEach((item) => {
    const parsed = PrePaySettingsSchema.safeParse(item)
    if (parsed.success) {
      methods.push(parsed.data)
    } else {
      unsupported.push(item)
    }
  })
  return { methods, unsupported }
}

// Drops unrecognized entries instead of failing the whole parse
export const PrePayMethodsSchema = z
  .array(z.unknown())
  .transform((items) => splitPrePayMethods(items).methods)

export const hasPrePayMethod = (
  methods: readonly PrePaySettings[] | null | undefined,
  key: PrePaySettings['key']
) => methods?.some((method) => method.key === key) ?? false

export const PaymentInputSchema = z.object({
  orderIds: z.array(z.number()),
  extra: z
    .object({
      orders: z.array(OrderResponseSchema).optional(),
      discounts: z.array(DiscountResponseSchema).optional(),
      frameIds: z.array(z.number().or(z.string())).optional(),
    })
    .optional(),
  merged: z.boolean().optional(),
  paymentMethod: z.nativeEnum(PaymentMethod),
  provider: z.nativeEnum(PaymentProvider).optional(),
  reader_id: z.string().or(z.number()).nullish(),
  iban: z.string().nullish(),
  holder: z.string().nullish(),
  customAmount: z.number(),
  sign: z.string().nullish(),
  inquiryId: z.string().min(1).optional(),
  cofidisApplication: CofidisApplicationFormPayloadSchema.optional(),
})

export type PaymentInput = z.infer<typeof PaymentInputSchema>

export const UnzerPlanCurrencySchema = z.object({
  id: z.number(),
  name: z.string(),
  symbol: z.string(),
})

export const UnzerInstallmentSchema = z.object({
  date: z.string(),
  amount: z.number(),
})

export const UnzerPlanSchema = z.object({
  number_of_rates: z.number().int().positive(),
  total_amount: z.number(),
  nominal_interest_rate: z.number().optional().catch(undefined),
  effective_interest_rate: z.number().optional().catch(undefined),
  secci_url: z.string().url().optional().catch(undefined),
  rate_amount: z.number(),
  installments: z.array(UnzerInstallmentSchema).optional().catch(undefined),
})

export const UnzerPlanResponseSchema = z.object({
  code: z.number(),
  success: z.boolean(),
  inquiry_id: z.string().min(1),
  inquiry_expires_at: z.string(),
  amount: z.number().optional().catch(undefined),
  currency: UnzerPlanCurrencySchema.optional().catch(undefined),
  plan: UnzerPlanSchema,
  error: z.string().optional(),
  msg: z.string().optional(),
})

export type UnzerPlanResponse = z.infer<typeof UnzerPlanResponseSchema>

/***
 * {
      "id": 512,
      "reader_id": "tmr_FpPr3QSX6jWE1b",
      "reader_name": "false",
      "reader_serial_number": "WSC513111004733"
    }
 */
export const ReaderResponseSchema = z.object({
  id: z.number(),
  reader_id: z.string(),
  reader_name: z.string().nullable().catch(null),
  reader_serial_number: z.string(),
  client_device_id: z.string().nullish().catch(null),
})

export type ReaderResponse = z.infer<typeof ReaderResponseSchema>

// Raw response shape from ERP /payment/hesabe/terminal
export type HesabeReaderResponse = {
  id: number
  hesabe_terminal_id: string
  hesabe_terminal_name: string
  hesabe_terminal_type: string
  client_device_id: string
}

const BaseDeviceSchema = z.object({
  id: z.number(),
  reader_id: z.string(),
  reader_name: z.string().nullable().catch(null),
  reader_serial_number: z.string(),
  client_device_id: z.string().nullish().catch(null),
})

export const DeviceProviderSchema = z.enum(['stripe', 'hasabe']).catch('stripe')

export const StripeDeviceSchema = BaseDeviceSchema.extend({
  provider: z.literal('stripe'),
})

export const HesabeDeviceSchema = BaseDeviceSchema.extend({
  provider: z.literal('hasabe'),
  terminal_type: z.string(),
})

export const DeviceResponseSchema = z.discriminatedUnion('provider', [
  StripeDeviceSchema,
  HesabeDeviceSchema,
])

export type DeviceResponse = z.infer<typeof DeviceResponseSchema>
export type DeviceProvider = DeviceResponse['provider']

export type HesabeDeviceData = Omit<
  z.infer<typeof HesabeDeviceSchema>,
  'provider'
>

export const hesabeReaderToDevice = (
  r: HesabeReaderResponse
): HesabeDeviceData => ({
  id: r.id,
  reader_id: r.hesabe_terminal_id,
  reader_name: r.hesabe_terminal_name,
  reader_serial_number: r.hesabe_terminal_id,
  terminal_type: r.hesabe_terminal_type,
  client_device_id: r.client_device_id,
})

interface BasePaymentResponse {
  code: number
  error?: string
  msg?: string
}

export interface ErpInvoicePaymentResponse extends BasePaymentResponse {
  error: string
  success: boolean
  msg?: string
}

export interface InvoicePaymentResponse extends ErpInvoicePaymentResponse {
  method: 'invoice'
  provider: PaymentProvider
}

export interface ErpTerminalPaymentResponse extends BasePaymentResponse {
  payment_id: string | number
  msg?: string
}
export interface TerminalPaymentResponse extends ErpTerminalPaymentResponse {
  method: 'terminal'
  provider: PaymentProvider
}

export interface ErpMonthlyPaymentResponse extends BasePaymentResponse {
  success: boolean
  msg?: string
  error?: string
}

export interface ErpExternalProviderPaymentResponse
  extends ErpMonthlyPaymentResponse {
  link: string
  payment_id: string | number
}

export type ErpCofidisMonthlyPaymentResponse =
  ErpExternalProviderPaymentResponse

export type ErpAliorMonthlyPaymentResponse = ErpExternalProviderPaymentResponse

export interface MonthlyPaymentResponse extends ErpMonthlyPaymentResponse {
  method: 'monthly'
  provider: PaymentProvider
}

export interface ExternalMonthlyPaymentResponse extends MonthlyPaymentResponse {
  link: string
  payment_id: string | number
}

export type CofidisMonthlyPaymentResponse = ExternalMonthlyPaymentResponse

export type AliorMonthlyPaymentResponse = ExternalMonthlyPaymentResponse

export interface MergeTerminalPaymentResponse
  extends ErpTerminalPaymentResponse {
  method: 'mergeTerminal'
}

type PaymentNotificationStatus = {
  success: boolean
  message: string
  reason: string
}

export interface ErpPaylinkPaymentResponse extends BasePaymentResponse {
  payment_id: string | number
  link: string
  sms: PaymentNotificationStatus
  email: PaymentNotificationStatus
}

export interface PaylinkPaymentResponse extends ErpPaylinkPaymentResponse {
  method: 'paylink'
  provider: PaymentProvider
}

export type ResolvedMonthlyResponse<T extends PaymentProvider = 'sepa'> =
  T extends 'sepa' | 'unzer'
    ? MonthlyPaymentResponse
    : T extends 'cofidis' | 'cofidisApplication' | 'alior'
    ? ExternalMonthlyPaymentResponse
    : never

export type PaymentResponse<
  M extends PaymentMethod,
  P extends PaymentProvider
> = M extends 'invoice'
  ? InvoicePaymentResponse
  : M extends 'terminal'
  ? TerminalPaymentResponse
  : M extends 'paylink'
  ? PaylinkPaymentResponse
  : M extends 'monthly'
  ? ResolvedMonthlyResponse<P>
  : never

export const PaymentMetadataSchema = z.object({
  order_ids: z.array(z.number()),
  order_numbers: z.array(z.string()),
  prepay_method: PrePayMethodsSchema,
  pay_range: z.array(z.number()),
  prepaid_amount: z.number(),
  rest_amount: z.number(),
})

export type PaymentMetadata = z.infer<typeof PaymentMetadataSchema>

// A usable [min, max] from ERP's pay_range, or null
export const getPayRange = (
  range: readonly number[] | null | undefined
): [number, number] | null => {
  if (!range || range.length !== 2 || !range.every(Number.isFinite)) {
    return null
  }
  return [Math.min(range[0], range[1]), Math.max(range[0], range[1])]
}

// ERP answers unknown orders with zeroed balances; only prepay_method is usable
export const hasPaymentMetadataOrders = (
  metadata: PaymentMetadata | null | undefined
): metadata is PaymentMetadata => (metadata?.order_ids.length ?? 0) > 0

export type PaymentStatusResponse = {
  code: number
  msg: string
}

export type ExternalMonthlyStatusResponse = {
  code: string | number
  msg?: string
}

export type GlobeMedPaymentResponse = {
  success: boolean
  msg: string
  payment_id: number
}

export type HasabeTerminalStatusResponse = {
  status: 'SUCCESSFUL' | 'FAILED' | 'PENDING'
  msg: string
}

export const hasabeTerminalToStatusResponse = (
  status: HasabeTerminalStatusResponse
): PaymentStatusResponse => {
  if (status.status === 'SUCCESSFUL') {
    return { code: 200, msg: status.msg }
  }
  if (status.status === 'FAILED') {
    return { code: 400, msg: status.msg }
  }
  return { code: 202, msg: status.msg }
}

export const externalMonthlyToStatusResponse = (
  status: ExternalMonthlyStatusResponse
): PaymentStatusResponse => {
  // Wrap string codes to standard response
  if (typeof status.code === 'string') {
    if (status.code === 'paid') {
      return {
        code: 200,
        msg: 'Payment successful',
      } satisfies PaymentStatusResponse
    }
    return {
      code: 202,
      msg: 'Payment pending',
    } satisfies PaymentStatusResponse
  }

  return {
    code: status.code,
    msg: status.msg || 'Payment pending',
  } satisfies PaymentStatusResponse
}
